"use client";

import { Button } from "@workspace/ui/components/button";
// import Image from "next/image";
import Link from "next/link";
import posthog from "posthog-js";
import { useEffect } from "react";

/**
 * Error page component.
 *
 * Displays a user-friendly error message when an error occurs,
 * with navigation options to return to the home page.
 */
export default function ErrorPage({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Capture the error in PostHog for monitoring and debugging
    posthog.captureException(error);

    // Capture error page viewed event
    posthog.capture("error_page_viewed", {
      error_message: error.message,
      error_digest: error.digest,
      status_code: error.message.includes("404")
        ? "404"
        : error.message.includes("500")
          ? "500"
          : error.message.includes("403")
            ? "403"
            : error.message.includes("401")
              ? "401"
              : "500",
    });

    // Log the error to console in development
    console.error(error);
  }, [error]);

  // Extract status code from error message if available
  const getStatusCode = () => {
    if (error.message.includes("404")) return "404";
    if (error.message.includes("500")) return "500";
    if (error.message.includes("403")) return "403";
    if (error.message.includes("401")) return "401";
    return "500"; // Default fallback
  };

  // Get user-friendly error message
  const getErrorMessage = () => {
    const statusCode = getStatusCode();
    switch (statusCode) {
      case "404":
        return "Page not found";
      case "403":
        return "Access denied";
      case "401":
        return "Unauthorized";
      default:
        return "Something went wrong";
    }
  };

  // Get detailed description
  const getDescription = () => {
    const statusCode = getStatusCode();
    switch (statusCode) {
      case "404":
        return "Sorry, we couldn't find the page you're looking for. It might have been moved, deleted, or you entered the wrong URL.";
      case "403":
        return "You don't have permission to access this resource. Please contact your administrator if you believe this is an error.";
      case "401":
        return "You need to be authenticated to access this resource. Please sign in and try again.";
      default:
        return "An unexpected error occurred. Our team has been notified and is working to fix the issue.";
    }
  };

  const statusCode = getStatusCode();
  const errorMessage = getErrorMessage();
  const description = getDescription();

  return (
    <div className="min-h-[calc(100vh-69px)] max-h-[calc(100vh-69px)] flex items-center justify-center px-4 relative isolate overflow-hidden">
      <div className="relative z-20 text-center space-y-6 max-w-md">
        {/* Status Code */}
        <div className="space-y-2">
          <h1 className="text-8xl font-extrabold text-primary">{statusCode}</h1>
          <h2 className="text-2xl font-bold">{errorMessage}</h2>
        </div>

        {/* Description */}
        <p className="text-muted-foreground">{description}</p>

        {/* Error Details (only in development) */}
        {process.env.NODE_ENV === "development" && (
          <div className="text-left bg-muted p-4 rounded-lg">
            <p className="text-sm font-mono text-muted-foreground break-all">
              {error.message}
            </p>
            {error.digest && (
              <p className="text-xs text-muted-foreground mt-2">
                Error ID: {error.digest}
              </p>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={() => {
              posthog.capture("error_retry_clicked", {
                status_code: statusCode,
                error_message: error.message,
              });
              reset();
            }}
            variant="outline"
          >
            Try Again
          </Button>
          <Button asChild>
            <Link href="/">Return To Home</Link>
          </Button>
        </div>
      </div>
      {/* <div className="bg-gradient-to-b from-zinc-900 via-zinc-900/75 z-10 to-zinc-900 absolute inset-0" />
			<Image
				width={2000}
				height={2000}
				src="/53degrees_topography_dark.svg"
				alt="Topography"
				className="absolute inset-0 opacity-5 object-cover"
			/> */}
    </div>
  );
}
